import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { setAlert } from "../../../../actions/alert";
import "../bookingStyles.css";
import SectionTitle from "./SectionTitle";
import { cn } from "../../../../utils/classnames";
import { getChargeMethod } from "../../../../utils/flywire";
import ConfirmationModal from "../../components/ConfirmationModal";
import useConfirmation from "../../components/useConfirmation";
import {
    chargeToken, //chargeToken(id)
    chargeTokenEditedFee, //chargeTokenEditedFee({bookingId: id, editedFee: customFee, itineraryDetail: detailItinerary })
    chargeTokenAdditionalFee, //chargeTokenAdditionalFee({bookingId: id, additionalFee: additionalFee, itineraryDetail: detailItinerary })
    chargeTokenByCartId, //chargeTokenByCartId(id)
    chargeTokenWithRequestedChanges, //chargeTokenWithRequestedChanges(id)
    confirmBooking, //confirmBooking({id: id, {feeType: "EDITED_FEE", newAmount: newAmount, editedFeeMessage: editedFeeMessage}})
    confirmBookingByCartId, //confirmBookingByCartId(cartID)
    confirmAdditionalFee //confirmAdditionalFee({id: id, additionalFee: additionalFee, itineraryDetail: itineraryDetail})
} from "../../../../actions/admin/flywire"
import { updateBookingStatus, getBookingsByCartID } from "../../../../actions/admin/booking"
import { sendMailChangeCCInfo } from "../../../../actions/admin/mail"
import {
    LinkIcon,
    CloseCircleIcon,
    WarningIcon
} from "../../../icons"

const formatDateTimeCard = (input) => {
    if (!input) return "-";
    const date = new Date(input);
    if (Number.isNaN(date.getTime())) return "-";
    const month = date.toLocaleString("en-US", { month: "short" });
    const day = date.getDate();
    const year = date.getFullYear();
    const hour = String(date.getHours()).padStart(2, "0");
    const minute = String(date.getMinutes()).padStart(2, "0");
    return `${month} ${day}, ${year} ${hour}:${minute}`;
};

const formatJPY = (value) => {
    const amount = Number(value || 0);
    if (!amount) return "-";
    return `${amount.toLocaleString()} JPY`;
};

// How the money was actually taken, read back from the Flywire booking_reference prefix
// (CC = the whole cart in one payment, CQ = through a quotation, CB = this booking alone,
// and so on). Nothing else on a payment row says which of those happened.
const ChargeMethodFlag = ({ method }) => {
    if (!method) return null;

    return (
        <div
            title={method.reference}
            className="tw:mt-1.5 tw:inline-flex tw:items-center tw:gap-1 tw:rounded-[4px] tw:border tw:border-[#E8E8E8] tw:bg-[#F9F9F9] tw:px-2 tw:py-[3px] tw:font-hanken-grotesk tw:text-[11px] tw:font-[600] tw:leading-[14px] tw:text-[#8D8D8D]"
        >
            <span className="tw:text-[#B5B5B5]">{method.code}</span>
            <span>{method.label}</span>
        </div>
    );
};

/**
 * The amount owed on a booking — the figure the charge endpoints actually put through
 * Flywire (routes/api/flywire.js), so it is also the amount that was collected once the
 * booking reads CHARGED. The price fields are not uniform:
 *   - a booking created from a quotation keeps the PRE-discount figure in `totalPrice`
 *     and the amount owed in `discountedPrice`;
 *   - every other booking already carries the discounted figure in `totalPrice`;
 *   - an edited-fee charge/confirm overwrites `totalPrice` with the price the admin set
 *     and leaves `discountedPrice` stale, so that case wins over the quotation rule.
 */
const getAmountOwed = (booking) => {
    if (booking?.editedFeeType === "CHARGE_EDITED_FEE" || booking?.confirmWithEditedFee) {
        return Number(booking?.totalPrice || 0);
    }
    if (booking?.discount?.fromQuotation) {
        return Number(booking?.discountedPrice || 0);
    }
    return Number(booking?.totalPrice || 0);
};

const PaymentDetail = ({
    booking,
    showLoading,
    closeLoading
}) => {
    const [newAmount, setNewAmount] = useState(0);
    const [detailItinerary, setDetailItinerary] = useState('');
    const [additionalFee, setAdditionalFee] = useState(0);
    const { confirm, confirmationProps } = useConfirmation();
    const [showDetailStates, setShowDetailStates] = useState({});

    // Every booking sharing this cart. A cart holding more than one booking can be
    // settled in a single Flywire charge / a single invoice write, which is what the
    // "whole cart" action below does; a one-booking cart has nothing extra to offer.
    const [cartBookings, setCartBookings] = useState([]);

    const dispatch = useDispatch();
    const isDeferredPayment = booking?.customer?.user?.deferredPaymentEligible || false;

    const getShowDetail = (key) => showDetailStates[key] || false;
    const toggleShowDetail = (key) => setShowDetailStates(prev => ({ ...prev, [key]: !prev[key] }));

    useEffect(() => {
        let cancelled = false;

        if (!booking?.cartID) {
            setCartBookings([]);
            return;
        }

        (async () => {
            const bookings = await getBookingsByCartID(booking.cartID);
            if (!cancelled) setCartBookings(Array.isArray(bookings) ? bookings : []);
        })();

        return () => { cancelled = true; };
    }, [booking?.cartID]);

    const paymentStatus = String(booking?.paymentStatus || "").toUpperCase();
    const paymentMethodStatus = String(booking?.paymentMethodStatus || "").toUpperCase();
    const bookingStatus = String(booking?.bookingStatus || "").toUpperCase();
    const isCancelledBooking = bookingStatus === "CANCELLED";

    // What the customer will actually be charged — never the pre-discount `totalPrice`.
    const chargeableAmount = getAmountOwed(booking);

    // A "whole cart" settlement puts every booking sharing the cart through one Flywire
    // charge (/charge-token-by-cart-id) or one monthly-invoice write
    // (/confirm-booking-by-cart-id). Both endpoints refuse unless EVERY booking in the
    // cart is still CHARGE_PENDING + CONFIRMED and none came from a quotation, so the
    // action is only offered when those same conditions hold — otherwise the page would
    // present a button the API rejects.
    const isMultiBookingCart = cartBookings.length > 1;
    const isCartFromQuotation = cartBookings.some((item) => item?.fromQuotation && item?.quotation);
    const isCartSettleable =
        isMultiBookingCart &&
        !isCartFromQuotation &&
        !booking?.chargeFromQuotation &&
        cartBookings.every(
            (item) =>
                String(item?.paymentStatus || "").toUpperCase() === "CHARGE_PENDING" &&
                String(item?.bookingStatus || "").toUpperCase() === "CONFIRMED"
        );
    const cartChargeableAmount = cartBookings.reduce((sum, item) => sum + getAmountOwed(item), 0);

    // Flywire accepted the charge call and then reported a failure on its webhook. The
    // booking keeps whatever edit was applied; only the money never arrived.
    //
    // "CHANGED" means the customer replaced the card that failed, which clears that: the
    // booking holds a new token, is still unpaid, and charges again like any pending one,
    // so it reads as CHARGE_PENDING throughout and the charge form comes back.
    //
    // Mirrors isChargeableAfterCardChange in routes/api/flywire.js — the two must agree or
    // the page offers a charge the API rejects. The timestamps guard only the repeat case:
    // paymentMethodStatus is never cleared, so it still reads CHANGED after a later decline
    // and alone would hide it. Missing stamps leave nothing to compare, so the flag stands.
    const cardUpdatedAt = new Date(booking?.paymentMethodUpdatedAt).getTime();
    const lastChargeAt = new Date(booking?.flywireChargedAt).getTime();
    const isCardReplacedSinceLastCharge =
        !Number.isFinite(cardUpdatedAt) || !Number.isFinite(lastChargeAt)
            ? true
            : cardUpdatedAt > lastChargeAt;
    const isRetryableAfterCardChange =
        paymentStatus === "CHARGE_FAILED" &&
        paymentMethodStatus === "CHANGED" &&
        isCardReplacedSinceLastCharge;
    const effectivePaymentStatus = isRetryableAfterCardChange ? "CHARGE_PENDING" : paymentStatus;
    const isChargeFailed = effectivePaymentStatus === "CHARGE_FAILED";
    // Whether a charge has ever failed, which a card change does not undo. Drives the
    // Change Credit Card / Cancel Booking actions: replacing the card makes the booking
    // chargeable again but is no reason to take those away — the new card can decline too.
    const hasFailedCharge = paymentStatus === "CHARGE_FAILED";
    const failedChargeAmount = Number(booking?.flywireWebhookRequest?.data?.amount_to || 0);
    // clientReason is Flywire's plain-language explanation; reason/reasonCode are the raw
    // codes, kept alongside it because they are what support quotes back to Flywire.
    const failedChargeClientReason = booking?.clientReason || "";
    const failedChargeReason = [booking?.reason, booking?.reasonCode]
        .filter(Boolean)
        .join(" · ");

    const chargeTokenHandler = async (id, chargeCart = false, customFeeType = "", customFee = 0) => {
        if (!id) return

        showLoading(true);

        if (chargeCart) {
            const chargeTokenByCartIdRes = await chargeTokenByCartId(id)
            closeLoading(true)

            if (chargeTokenByCartIdRes?.data?.status === "success") {
                window.location.reload()
            } else {
                dispatch(setAlert(chargeTokenByCartIdRes?.data?.message || "Failed to charge token. Please try again.", "danger"))
            }
        } else {
            if (customFeeType === "edited") {
                const chargeTokenRes = await chargeTokenEditedFee({
                    bookingId: id,
                    editedFee: customFee,
                    itineraryDetail: detailItinerary
                })
                closeLoading(true)

                if (chargeTokenRes?.data?.status === "success") {
                    window.location.reload()
                } else {
                    dispatch(setAlert(chargeTokenRes?.data?.message || "Failed to charge edited fee. Please try again.", "danger"))
                }
            } else if (customFeeType === "additional") {
                const chargeTokenRes = await chargeTokenAdditionalFee({
                    bookingId: id,
                    additionalFee: customFee,
                    itineraryDetail: detailItinerary
                });
                closeLoading(true)

                if (chargeTokenRes?.data?.status === "success") {
                    window.location.reload()
                } else {
                    dispatch(setAlert(chargeTokenRes?.data?.message || "Failed to charge additional fee. Please try again.", "danger"))
                }
            } else {
                const chargeTokenRes = await chargeToken(id)
                closeLoading(true)

                if (chargeTokenRes?.data?.status === "success") {
                    window.location.reload()
                } else {
                    dispatch(setAlert(chargeTokenRes?.data?.message || "Failed to charge token. Please try again.", "danger"))
                }
            }
        }
    }

    const confirmBookingHandler = async (id, byCartId = false, feeType = "", newAmount = 0, editedFeeMessageParams = "") => {
        if (!id) return
        showLoading(true)

        if (byCartId) {
            // `id` is the cartID here — every booking in the cart is added to the
            // account's monthly invoice in one call.
            const confirmCartRes = await confirmBookingByCartId(id)
            closeLoading(true)

            if (confirmCartRes?.status === "SUCCESS") {
                window.location.reload()
            } else {
                dispatch(setAlert(confirmCartRes?.message || "Failed to add the cart to the monthly invoice. Please try again.", "danger"))
            }
        } else {
            if (feeType === "EDITED_FEE") {
                let reqBody = {
                    bookingId: id
                }
                reqBody.feeType = "EDITED_FEE";
                reqBody.newAmount = newAmount;
                reqBody.editedFeeMessage = editedFeeMessageParams

                const confirmBookingRes = await confirmBooking(reqBody)
                closeLoading(true)

                if (confirmBookingRes?.status === "SUCCESS") {
                    window.location.reload()
                } else {
                    dispatch(setAlert(confirmBookingRes?.message || "Failed to confirm booking with edited fee. Please try again.", "danger"))
                }
            } else if (feeType === "ADDITIONAL_FEE") {
                let reqBody = {
                    bookingId: id,
                    additionalFee: newAmount,
                    itineraryDetail: detailItinerary
                }

                const confirmAdditionalFeeRes = await confirmAdditionalFee(reqBody)
                closeLoading(true)

                if (confirmAdditionalFeeRes?.status === "SUCCESS") {
                    window.location.reload()
                } else {
                    dispatch(setAlert(confirmAdditionalFeeRes?.message || "Failed to confirm additional fee. Please try again.", "danger"))
                }
            } else {
                const confirmBookingRes = await confirmBooking({ bookingId: id })
                closeLoading(true)

                if (confirmBookingRes?.status === "SUCCESS") {
                    window.location.reload()
                } else {
                    dispatch(setAlert(confirmBookingRes?.message || "Failed to confirm booking. Please try again.", "danger"))
                }
            }
        }
    }

    const cancelBookingHandler = async (id) => {
        if (!id) return

        showLoading(true)

        const isSuccess = await dispatch(updateBookingStatus(id, "CANCELLED", null))
        closeLoading(true)

        // updateBookingStatus raises its own alert when it fails.
        if (isSuccess) {
            window.location.reload()
        }
    }

    // Emails the customer a tokenised link to re-enter their card, and flags every booking
    // in the cart as awaiting that change.
    const changeCCInfoHandler = async (cartID) => {
        if (!cartID) {
            dispatch(setAlert("This booking has no cart reference, so the email cannot be sent.", "danger"))
            return
        }

        showLoading(true)

        const res = await sendMailChangeCCInfo(cartID)
        closeLoading(true)

        if (res?.msg === "SUCCESS") {
            window.location.reload()
        } else {
            dispatch(setAlert("Failed to send the change credit card email. Please try again.", "danger"))
        }
    }

    // Both of these appear twice — once on a live booking and once on a failed-charge
    // booking — so the dialog is described here rather than at each button.
    const askCancelBooking = () => confirm({
        title: "Cancel this booking?",
        description: "This cannot be undone.",
        variant: "danger",
        confirmLabel: "Cancel Booking",
        cancelLabel: "Back",
        onConfirm: () => cancelBookingHandler(booking?._id),
    });

    const askChangeCreditCard = () => confirm({
        title: "Send this customer an email to change their credit card?",
        description: "They get a one-time link to re-enter their card. Every booking in this cart is flagged as awaiting the new card.",
        confirmLabel: "Send Email",
        cancelLabel: "Back",
        onConfirm: () => changeCCInfoHandler(booking?.cartID),
    });

    // Normalise requestChangeBooking regardless of shape (array | object | keyed with 's')
    const getRequestChangeBookingList = () => {
        const rcb = booking?.requestChangeBooking ?? booking?.requestChangeBookings;
        if (Array.isArray(rcb)) return rcb;
        if (rcb && typeof rcb === "object") return [rcb];
        return [];
    };

    // An edit approved while the booking was still CHARGE_PENDING charges the whole new
    // price against the booking itself, so the booking — not the pre-edit snapshot — holds
    // the only payment, and that charge is the booking's own rather than a modification on
    // top of an earlier one.
    const isUnpaidSnapshot = (request) =>
        request?.requestType === "reschedule" &&
        String(request?.previousBooking?.paymentStatus || "").toUpperCase() === "CHARGE_PENDING";

    // The oldest approved reschedule holds the pre-edit payment data, if there was any.
    const oldestApprovedReschedule = getRequestChangeBookingList()
        .filter(item => item?.requestType === "reschedule" && item?.status === "APPROVED")
        .sort((a, b) => new Date(a.requestCreatedAt || 0) - new Date(b.requestCreatedAt || 0))[0];

    // True when the booking was first paid for by an edit rather than by a plain confirm.
    const confirmedByEdit = Boolean(oldestApprovedReschedule) && isUnpaidSnapshot(oldestApprovedReschedule);

    const buildChargeTimeline = () => {
        const entries = [];

        // previousBooking is only a payment source when it was actually paid; if the edit
        // is what paid for the booking, the current booking carries the charge data.
        const sourceBooking = (
            confirmedByEdit ? booking : oldestApprovedReschedule?.previousBooking
        ) ?? booking;

        // 1. Initial booking charge
        const isEditedFee = sourceBooking?.editedFeeType === "CHARGE_EDITED_FEE";
        // A booking charged through its quotation (webhook reference CQ) only gets
        // paymentStatus/flywireChargedAt back — the charge response stays on the quotation —
        // so the fallback has to be the amount owed, not the pre-discount `totalPrice`.
        entries.push({
            key: "initial",
            label: confirmedByEdit
                ? "Booking Updated & Confirmed"
                : isEditedFee ? "Booking Confirmed (Price Adjusted)" : "Booking Confirmed",
            isEditedFee: isEditedFee,
            amount: Number(sourceBooking?.flywireChargeResponse?.amount ?? getAmountOwed(sourceBooking)),
            date: formatDateTimeCard(sourceBooking?.flywireChargedAt),
            account: sourceBooking?.chargedAccount || sourceBooking?.flywireChargeAccount || "TLF",
            status: sourceBooking?.chargedStatus || sourceBooking?.flywireStatus || "Guaranteed",
            paymentId: sourceBooking?.flywirePaymentId || "-",
            chargeMethod: getChargeMethod({
                webhookRequest: sourceBooking?.flywireWebhookRequest,
                chargeRequest: sourceBooking?.flywireChargeRequest,
            }),
            useDetail: isEditedFee,
            detailTitle: "Reason for Price Adjustment",
            detailBody: sourceBooking?.chargeWithEditedFeeDetailItinerary || "",
        });

        // 2. Additional fee on original booking
        if (sourceBooking?.chargeAdditionalFee) {
            entries.push({
                key: "additional",
                label: "Additional Fee Charged",
                amount: Number(sourceBooking?.flywireChargeAdditionalFeeResponse?.amount ?? sourceBooking?.additionalFee ?? 0),
                date: formatDateTimeCard(sourceBooking?.flywireChargeAdditionalFeeAt),
                account: sourceBooking?.chargedAccount || sourceBooking?.flywireChargeAccount || "TLF",
                status: sourceBooking?.chargedStatus || sourceBooking?.flywireStatus || "Guaranteed",
                paymentId: sourceBooking?.flywirePaymentId || "-",
                // The additional-fee charge is a payment of its own, with its own AF
                // reference — reading the booking's main webhook here would mislabel it.
                chargeMethod: getChargeMethod({
                    webhookRequest: sourceBooking?.flywireAdditionalFeeWebhookRequest,
                    chargeRequest: sourceBooking?.flywireChargeAdditionalFeeRequest,
                }),
                useDetail: true,
                detailTitle: "Reason for Additional Fee",
                detailBody: sourceBooking?.chargeAdditionalFeeDetailItinerary || "",
            });
        }

        // 3. Approved modification charges.
        // A full charge from an edit is the booking's own payment, already listed above as
        // "Booking Updated & Confirmed" — listing it here too would double-count it and
        // mislabel it as a client-side modification request.
        getRequestChangeBookingList()
            .filter(item => item?.status === "APPROVED" && item?.paymentStatus === "CHARGED")
            .filter(item => !isUnpaidSnapshot(item))
            .sort((a, b) => new Date(a.requestCreatedAt || 0) - new Date(b.requestCreatedAt || 0))
            .forEach((item, idx) => {
                entries.push({
                    key: `modification_${idx}`,
                    label: item?.requestType === "cancel" ? "Cancellation Charge Collected" : "Booking Modified",
                    amount: Number(item?.flywireChargeResponse?.amount ?? item?.chargeAmount ?? 0),
                    date: formatDateTimeCard(item?.chargedAt || item?.respondedAt),
                    account: "TLF",
                    status: "Guaranteed",
                    paymentId: item?.flywirePaymentId || "-",
                    chargeMethod: getChargeMethod({
                        webhookRequest: item?.flywireWebhookRequest,
                        chargeRequest: item?.flywireChargeRequest,
                    }),
                    useDetail: false,
                    detailTitle: "",
                    detailBody: "",
                });
            });

        return entries;
    };

    const buildDeferredTimeline = () => {
        const allItems = booking?.invoice?.items || [];
        const items = allItems.filter(item =>
            String(item?.bookingId) === String(booking?._id) ||
            String(item?.refId) === String(booking?._id)
        );

        return items.map((item, index) => {
            const isLast = index === items.length - 1;
            const runningTotal = items.slice(0, index + 1).reduce((acc, cur) => acc + Number(cur?.amount || 0), 0);

            let title = "";
            let eventDesc = "";
            let useDetail = false;
            let detailTitle = "";

            switch (item?.type) {
                case "booking":
                    title = confirmedByEdit ? "Booking Updated & Confirmed" : "Booking Confirmed";
                    eventDesc = "Added to Monthly Invoice";
                    break;
                case "bookingeditfee":
                    title = "Booking Confirmed (Price Adjusted)";
                    eventDesc = "Added to Monthly Invoice";
                    useDetail = true;
                    detailTitle = "Reason for Price Adjustment";
                    break;
                case "additionalfee":
                    title = "Additional Fee";
                    eventDesc = "Added to Monthly Invoice";
                    useDetail = true;
                    detailTitle = "Reason for Additional Fee";
                    break;
                case "cancelfee":
                    title = "Modification Approved";
                    eventDesc = "Added to Monthly Invoice";
                    break;
                default:
                    title = item?.type || "Charge";
                    eventDesc = "Added to Monthly Invoice";
            }

            return {
                key: `deferred_${index}`,
                title,
                eventDesc,
                date: formatDateTimeCard(item?.addedAt),
                amount: isLast ? runningTotal : Number(item?.amount || 0),
                isLast,
                useDetail,
                detailTitle,
                detailBody: item?.type === "bookingeditfee"
                    ? (booking?.confirmWithEditedFeeDetailItinerary || "")
                    : (booking?.confirmAdditionalFeeDetailItinerary || ""),
            };
        });
    };

    // `amount` is the amount actually charged/owed (see chargeableAmount), not the
    // pre-discount total — a discounted booking must not advertise the higher figure.
    const getPaymentStatusLabel = (status, deferredPayment, amount) => {
        const amountText = `${Number(amount || 0).toLocaleString()} JPY`;

        if (status === "CHARGE_PENDING") {
            if (deferredPayment) {
                return {
                    text: `¥ Add ${amountText} To Monthly Total`,
                    className: "tw:bg-[#3CAEA3] tw:text-[#FFFFFF]"
                }
            } else {
                return {
                    text: `¥ Charge ${amountText}`,
                    className: "tw:bg-[#3CAEA3] tw:text-[#FFFFFF]"
                }
            }
        } else if (status === "ADDED_TO_MONTHLY_TOTAL") {
            return {
                text: `Added ${amountText} To Monthly Total`,
                className: "tw:bg-[#FFF8E8] tw:text-[#BF993A]"
            }
        } else if (status === "CHARGED") {
            return {
                text: `Charged ${amountText}`,
                className: "tw:bg-[#DFF9F7] tw:text-[#3CAEA3]"
            }
        }

        return {
            text: "Pending",
            className: "tw:bg-[#F3F3F3] tw:text-[#8D8D8D]"
        }
    }

    const cancelledChargeRows = [booking].filter((item) => ["CHARGED", "ADDED_TO_MONTHLY_TOTAL"].includes(String(item?.paymentStatus || "").toUpperCase()));

    // What was actually collected, so the fallback refund is not inflated by the
    // pre-discount price a quotation booking keeps in `totalPrice`.
    const totalChargedAmount = cancelledChargeRows.reduce((sum, item) => sum + getAmountOwed(item), 0);
    const cancellationFeeAmount = Number(
        booking?.requestChangeBooking?.totalCancellationFee ||
        booking?.totalCancellationFee ||
        booking?.requestChangeBooking?.cancellationFee ||
        booking?.cancellationFee ||
        0
    );
    const refundAmount = Number(
        booking?.requestChangeBooking?.refundAmount ||
        booking?.refundAmount ||
        Math.max(totalChargedAmount - cancellationFeeAmount, 0)
    );

    const chargeTimeline = buildChargeTimeline();
    const deferredTimeline = buildDeferredTimeline();
    const totalPaymentReceived = paymentStatus === "CHARGED"
        ? chargeTimeline.reduce((sum, e) => sum + e.amount, 0)
        : deferredTimeline.length > 0
            ? deferredTimeline[deferredTimeline.length - 1].amount
            : 0;

    // Bookings that belong to a quotation with standalone items can only be charged
    // from the quotation detail page — open it in a new tab.
    const openQuotationDetail = () => {
        const quotationId = booking?.quotation?._id || booking?.quotation;
        if (!quotationId) return;
        window.open(`/admin/quotations/create/${quotationId}`, "_blank", "noopener,noreferrer");
    }

    return (
        <>
            <ConfirmationModal {...confirmationProps} />
            <SectionTitle title="Payment Details" />
            {isCancelledBooking ? (
                <>
                    <div className="tw:mb-2 tw:w-full tw:overflow-hidden tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-white tw:px-5 tw:py-6">
                        {cancelledChargeRows.map((row, index) => (
                            <div key={row?._id || `cancelled-charge-${index}`}>
                                <div className="tw:flex tw:flex-col tw:gap-4 tw:md:flex-row tw:md:items-start tw:md:gap-[54px]">
                                    <div className="tw:w-full tw:md:w-[150px] tw:font-hanken-grotesk">
                                        <div className="tw:text-[14px] tw:font-normal tw:leading-[20px] tw:text-[#7C7B7B]">
                                            {index === 0 ? "Booking Confirmed" : "Booking Modified"}
                                        </div>
                                        <ChargeMethodFlag
                                            method={getChargeMethod({
                                                webhookRequest: row?.flywireWebhookRequest,
                                                chargeRequest: row?.flywireChargeRequest,
                                            })}
                                        />
                                    </div>
                                    <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-4 tw:md:flex-row tw:md:gap-9">
                                        <div className="tw:inline-flex tw:h-[45px] tw:items-center tw:justify-center tw:rounded-[5px] tw:bg-[#DFF9F7] tw:px-5 tw:py-[5px] tw:font-hanken-grotesk tw:text-[15px] tw:font-normal tw:leading-[22px] tw:text-[#3CAEA3]">
                                            {`Charged ${getAmountOwed(row).toLocaleString()} JPY`}
                                        </div>
                                        <div className="tw:min-w-0 tw:flex-1 tw:font-hanken-grotesk">
                                            <div className="tw:text-[13px] tw:font-normal tw:leading-[18.5px] tw:text-[#8D8D8D]">Account</div>
                                            <div className="tw:truncate tw:text-[15px] tw:font-normal tw:leading-[22px] tw:text-[#282828]">{row?.chargedAccount || row?.flywireChargeAccount || "TLF"}</div>
                                        </div>
                                        <div className="tw:min-w-0 tw:flex-1 tw:font-hanken-grotesk">
                                            <div className="tw:text-[13px] tw:font-normal tw:leading-[18.5px] tw:text-[#8D8D8D]">Status</div>
                                            <div className="tw:truncate tw:text-[15px] tw:font-bold tw:leading-[22px] tw:text-[#3CAEA3]">{row?.chargedStatus || row?.flywireStatus || "Guaranteed"}</div>
                                        </div>
                                        <div className="tw:min-w-0 tw:flex-1 tw:font-hanken-grotesk">
                                            <div className="tw:text-[13px] tw:font-normal tw:leading-[18.5px] tw:text-[#8D8D8D]">Charged On</div>
                                            <div className="tw:truncate tw:text-[15px] tw:font-normal tw:leading-[22px] tw:text-[#282828]">{formatDateTimeCard(row?.chargedAt || row?.updatedAt)}</div>
                                        </div>
                                        <div className="tw:min-w-0 tw:flex-1 tw:font-hanken-grotesk">
                                            <div className="tw:text-[13px] tw:font-normal tw:leading-[18.5px] tw:text-[#8D8D8D]">Payment ID</div>
                                            <div className="tw:truncate tw:text-[15px] tw:font-normal tw:leading-[22px] tw:text-[#282828]">{row?.paymentId || "-"}</div>
                                        </div>
                                    </div>
                                </div>
                                {index < cancelledChargeRows.length - 1 ? <div className="tw:my-6 tw:h-px tw:w-full tw:bg-[#E8E8E8]" /> : null}
                            </div>
                        ))}
                    </div>

                    <div className="tw:mb-5 tw:w-full tw:overflow-hidden tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-white tw:px-5 tw:py-6">
                        <div className="tw:flex tw:flex-col tw:gap-4 tw:md:flex-row tw:md:items-start tw:md:gap-[54px]">
                            <div>
                                <div className="tw:w-[150px] tw:font-hanken-grotesk tw:text-[15px] tw:font-bold tw:leading-[22px] tw:text-[#4E4E4E]">Refund</div>
                                <div className="tw:font-hanken-grotesk tw:text-[13px] tw:font-normal tw:leading-[18.5px] tw:text-[#8D8D8D]">Booking Cancelled</div>
                            </div>
                            <div className="tw:flex tw:flex-col tw:gap-4 tw:md:flex-row tw:md:items-start tw:md:gap-9">
                                <div>
                                    <div className="tw:inline-flex tw:h-[45px] tw:items-center tw:justify-center tw:gap-2 tw:rounded-[5px] tw:bg-black tw:px-6 tw:py-[5px] tw:font-hanken-grotesk tw:text-[15px] tw:font-bold tw:leading-[22px] tw:text-white">
                                        <span>¥</span>
                                        <span>{`Refund ${refundAmount.toLocaleString()} JPY`}</span>
                                    </div>
                                </div>
                                <div className="tw:w-[140px] tw:font-hanken-grotesk">
                                    <div className="tw:text-[13px] tw:font-normal tw:leading-[18.5px] tw:text-[#8D8D8D]">Account</div>
                                    <div className="tw:text-[15px] tw:font-normal tw:leading-[22px] tw:text-[#282828]">TLF</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : (paymentStatus === "CHARGED" || paymentStatus === "ADDED_TO_MONTHLY_TOTAL") ? (
                <>
                    {/* Payment History Timeline */}
                    <div className="tw:mb-2 tw:w-full tw:overflow-hidden tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-white">
                        {/* Card header */}
                        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:border-b tw:border-[#E8E8E8] tw:px-5 tw:py-4">
                            <span className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[700] tw:text-[#4E4E4E]">Payment History</span>
                            {
                                process.env.REACT_APP_INVOICE_MENU === "true" && (
                                    paymentStatus === "ADDED_TO_MONTHLY_TOTAL" && booking?.invoice?._id && (
                                        <a
                                            href={`/admin/invoices/${booking.invoice._id}`}
                                            className="tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D] tw:underline"
                                        >
                                            Invoice Payment:{" "}
                                            {booking.invoice.paymentStatus === "PAID"
                                                ? "PAID"
                                                : booking.invoice.paymentStatus === "CHARGE_PENDING"
                                                    ? "CHARGE PENDING"
                                                    : "UNPAID"}
                                        </a>
                                    )
                                )
                            }
                        </div>

                        {paymentStatus === "CHARGED" ? (
                            /* ── Advance Payment Timeline ── */
                            chargeTimeline.map((entry, index, arr) => (
                                <div key={entry.key}>
                                    <div className="tw:flex tw:flex-col tw:gap-4 tw:px-5 tw:py-5 tw:md:flex-row tw:md:items-start tw:md:gap-[54px]">
                                        {/* Left: event label */}
                                        <div className="tw:w-full tw:shrink-0 tw:font-hanken-grotesk tw:md:w-[170px]">
                                            <div className="tw:text-[14px] tw:font-normal tw:leading-[20px] tw:text-[#7C7B7B]">{entry.label}</div>
                                            {entry.key === "initial" && entry.isEditedFee && (
                                                <div className="tw:text-[13px] tw:font-[700] tw:italic tw:text-[#7C7B7B]">Edited Booking Cost</div>
                                            )}
                                            <ChargeMethodFlag method={entry.chargeMethod} />
                                        </div>

                                        {/* Right: amount + meta */}
                                        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-4 tw:md:flex-row tw:md:flex-wrap tw:md:gap-9">
                                            {/* Charged amount as green button */}
                                            <div className="tw:shrink-0 tw:font-hanken-grotesk">
                                                <div className="tw:inline-flex tw:h-[45px] tw:items-center tw:justify-center tw:rounded-[5px] tw:bg-[#DFF9F7] tw:px-5 tw:py-[5px] tw:text-[15px] tw:font-normal tw:leading-[22px] tw:text-[#3CAEA3]">
                                                    {entry.amount > 0 ? `Charged ${entry.amount.toLocaleString()} JPY` : "—"}
                                                </div>
                                            </div>

                                            <div className="tw:min-w-0 tw:flex-1 tw:font-hanken-grotesk">
                                                <div className="tw:text-[13px] tw:font-normal tw:leading-[18.5px] tw:text-[#8D8D8D]">Account</div>
                                                <div className="tw:truncate tw:text-[15px] tw:font-normal tw:leading-[22px] tw:text-[#282828]">{entry.account}</div>
                                            </div>

                                            <div className="tw:min-w-0 tw:flex-1 tw:font-hanken-grotesk">
                                                <div className="tw:text-[13px] tw:font-normal tw:leading-[18.5px] tw:text-[#8D8D8D]">Status</div>
                                                <div className="tw:truncate tw:text-[15px] tw:font-bold tw:leading-[22px] tw:text-[#3CAEA3]">{entry.status}</div>
                                            </div>

                                            <div className="tw:min-w-0 tw:flex-1 tw:font-hanken-grotesk">
                                                <div className="tw:text-[13px] tw:font-normal tw:leading-[18.5px] tw:text-[#8D8D8D]">Charged On</div>
                                                <div className="tw:truncate tw:text-[15px] tw:font-normal tw:leading-[22px] tw:text-[#282828]">{entry.date || "—"}</div>
                                            </div>

                                            <div className="tw:min-w-0 tw:flex-1 tw:font-hanken-grotesk">
                                                <div className="tw:text-[13px] tw:font-normal tw:leading-[18.5px] tw:text-[#8D8D8D]">Payment ID</div>
                                                <div className="tw:truncate tw:text-[15px] tw:font-normal tw:leading-[22px] tw:text-[#282828]">{entry.paymentId || "—"}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expandable detail (price adjustment / additional fee explanation) */}
                                    {entry.useDetail && (
                                        <div className="tw:px-5 tw:pb-5 tw:md:pl-[calc(170px+54px)]">
                                            <button
                                                type="button"
                                                className="tw:flex tw:items-center tw:gap-1.5 tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]"
                                                onClick={() => toggleShowDetail(entry.key)}
                                            >
                                                <span>{getShowDetail(entry.key) ? "Hide explanation" : "View explanation"}</span>
                                                <svg
                                                    width="10" height="6" viewBox="0 0 10 6" fill="none"
                                                    className={`tw:transition-transform tw:duration-200 ${getShowDetail(entry.key) ? "tw:rotate-180" : "tw:rotate-0"}`}
                                                >
                                                    <path d="M1 1L5 5L9 1" stroke="#8D8D8D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                            <div className={`tw:grid tw:transition-all tw:duration-300 tw:ease-in-out ${getShowDetail(entry.key) ? "tw:grid-rows-[1fr] tw:mt-2" : "tw:grid-rows-[0fr]"}`}>
                                                <div className="tw:overflow-hidden">
                                                    <div className="tw:rounded-[5px] tw:bg-[#F9F9F9] tw:p-4">
                                                        <p className="tw:mb-3 tw:font-hanken-grotesk tw:text-[12px] tw:font-[700] tw:text-[#8D8D8D]">{entry.detailTitle}</p>
                                                        <p className="tw:mb-0 tw:font-hanken-grotesk tw:text-[14px] tw:font-[400] tw:text-[#4E4E4E]">{entry.detailBody || "—"}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {index < arr.length - 1 && <div className="tw:mx-5 tw:h-px tw:bg-[#E8E8E8]" />}
                                </div>
                            ))
                        ) : (
                            /* ── Deferred Payment Timeline ── */
                            <>
                                {buildDeferredTimeline().length > 0 ? (
                                    buildDeferredTimeline().map((entry, index, arr) => (
                                        <div key={entry.key}>
                                            <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:px-5 tw:py-5">
                                                {/* Left: title + amount */}
                                                <div className="tw:flex tw:items-center tw:gap-4">
                                                    <span className="tw:font-hanken-grotesk tw:text-[14px] tw:font-[400] tw:text-[#8D8D8D]">
                                                        {entry.title}
                                                    </span>
                                                    <span className={`tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] ${entry.isLast ? "tw:text-[#282828]" : "tw:text-[#8D8D8D] tw:line-through"}`}>
                                                        {entry.amount.toLocaleString()} JPY
                                                    </span>
                                                </div>

                                                {/* Right: date + event + expand */}
                                                <div
                                                    className={`tw:flex tw:items-center tw:gap-2 ${entry.useDetail ? "tw:cursor-pointer" : ""}`}
                                                    onClick={() => entry.useDetail && toggleShowDetail(entry.key)}
                                                >
                                                    <span className="tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">
                                                        {entry.date} · {entry.eventDesc}
                                                    </span>
                                                    {entry.useDetail && (
                                                        <svg
                                                            width="10" height="6" viewBox="0 0 10 6" fill="none"
                                                            className={`tw:shrink-0 tw:transition-transform tw:duration-200 ${getShowDetail(entry.key) ? "tw:rotate-180" : "tw:rotate-0"}`}
                                                        >
                                                            <path d="M1 1L5 5L9 1" stroke="#A7A7A7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    )}
                                                </div>

                                                {/* Expandable detail */}
                                                {entry.useDetail && (
                                                    <div className={`tw:grid tw:w-full tw:transition-all tw:duration-300 tw:ease-in-out ${getShowDetail(entry.key) ? "tw:grid-rows-[1fr]" : "tw:grid-rows-[0fr]"}`}>
                                                        <div className="tw:overflow-hidden">
                                                            <div className="tw:mt-2 tw:rounded-[5px] tw:bg-[#F9F9F9] tw:p-4">
                                                                <p className="tw:mb-3 tw:font-hanken-grotesk tw:text-[12px] tw:font-[700] tw:text-[#8D8D8D]">{entry.detailTitle}</p>
                                                                <p className="tw:mb-0 tw:font-hanken-grotesk tw:text-[14px] tw:font-[400] tw:text-[#4E4E4E]">{entry.detailBody || "—"}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {index < arr.length - 1 && <div className="tw:mx-5 tw:h-px tw:bg-[#E8E8E8]" />}
                                        </div>
                                    ))
                                ) : (
                                    /* Fallback when invoice items aren't populated */
                                    <div className="tw:flex tw:flex-col tw:gap-1 tw:px-5 tw:py-5 tw:md:flex-row tw:md:items-center tw:md:gap-8">
                                        <div>
                                            <div className="tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">Booking No. of the Month</div>
                                            <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:text-[#282828]">{booking?.invoiceOrder || "—"}</div>
                                        </div>
                                        <div>
                                            <div className="tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">Monthly Total Amount</div>
                                            <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:text-[#282828]">{booking?.invoice?.totalAmount ? `${booking.invoice.totalAmount.toLocaleString()} JPY` : "—"}</div>
                                        </div>
                                        {booking?.addedToMonthlyTotalAt && (
                                            <div>
                                                <div className="tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">Added On</div>
                                                <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:text-[#282828]">{formatDateTimeCard(booking.addedToMonthlyTotalAt)}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="tw:mb-5 tw:overflow-hidden tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-white">
                        <div className="tw:flex tw:flex-col tw:gap-4 tw:p-4 tw:md:flex-row">
                            <div className="tw:w-[220px] tw:flex-shrink-0">
                                <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[700] tw:leading-[22px] tw:text-[#4E4E4E]">
                                    <div>Charge Additional</div>
                                    <div>Fee</div>
                                </div>
                                <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:text-[#8D8D8D]">
                                    After the Transfer
                                </div>
                            </div>

                            <div className="tw:flex-1 tw:space-y-3">
                                <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2">
                                    <div>
                                        <div className="tw:mb-1 tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">Total Payment Received</div>
                                        <div className="tw:flex tw:h-[50px] tw:w-full tw:items-center tw:rounded-[5px] tw:bg-[#F3F3F3] tw:px-4 tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:text-[#282828]">
                                            {formatJPY(totalPaymentReceived)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="tw:mb-1 tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">Additional Fee</div>
                                        <input
                                            type="text"
                                            placeholder="Amount"
                                            className="tw:h-[50px] tw:w-full tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-white tw:px-3 tw:py-2 tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:text-[#282828] tw:outline-none"
                                            value={additionalFee === 0 || additionalFee === '0' || additionalFee === '' ? '' : Number(additionalFee).toLocaleString()}
                                            onChange={(e) => {
                                                const raw = e.target.value.replace(/,/g, '');
                                                if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                                                    setAdditionalFee(raw);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div className="tw:mb-1 tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">Details</div>
                                    <textarea
                                        rows={7}
                                        placeholder="Explanation for the Price Adjustment"
                                        className="tw:!w-full tw:!rounded-[5px] tw:!border tw:!border-[#E8E8E8] tw:!bg-white tw:!px-3 tw:!py-2 tw:!font-hanken-grotesk tw:!text-[15px] tw:!placeholder:text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!outline-none"
                                        value={detailItinerary}
                                        onChange={(e) => setDetailItinerary(e.target.value)}
                                    />
                                </div>
                                <div className="tw:font-hanken-grotesk tw:text-[13px] tw:italic tw:font-[400] tw:text-[#8D8D8D]">*Sent to the client via email</div>
                                <button
                                    type="button"
                                    disabled={(!additionalFee || Number(additionalFee) === 0)}
                                    onClick={() => {
                                        confirm(isDeferredPayment
                                            ? {
                                                title: "Add this additional fee to the monthly total?",
                                                description: `${Number(additionalFee || 0).toLocaleString()} JPY is added to the customer's monthly invoice, and they are emailed the reason.`,
                                                confirmLabel: "Add Fee",
                                                onConfirm: () => confirmBookingHandler(booking?._id, false, "ADDITIONAL_FEE", additionalFee, detailItinerary),
                                            }
                                            : {
                                                title: "Charge this additional fee?",
                                                description: `${Number(additionalFee || 0).toLocaleString()} JPY is charged to the card on file, and the customer is emailed the reason.`,
                                                confirmLabel: "Charge Fee",
                                                onConfirm: () => chargeTokenHandler(booking?._id, false, "additional", additionalFee, detailItinerary),
                                            });
                                    }}
                                >
                                    <div
                                        className={`tw:inline-flex tw:h-[50px] tw:min-w-[210px] tw:items-center tw:justify-center tw:rounded-[5px] tw:px-5 tw:font-hanken-grotesk tw:text-[15px] tw:font-[600] tw:leading-[28px] tw:text-[#FFFFFF] ${(!additionalFee || Number(additionalFee) === 0)
                                            ? "tw:bg-[#3CAEA3] tw:opacity-50"
                                            : "tw:bg-[#3CAEA3]"
                                            }`}
                                    >
                                        {(!additionalFee || Number(additionalFee) === 0)
                                            ? isDeferredPayment
                                                ? "Confirm"
                                                : "Charge"
                                            : isDeferredPayment
                                                ? `Confirm ${Number(additionalFee).toLocaleString()} JPY`
                                                : `Charge ${Number(additionalFee).toLocaleString()} JPY`}
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="tw:mt-[52px] tw:gap-2 tw:mb-5 tw:flex tw:justify-end">
                        {!isDeferredPayment && (
                            <button
                                type="button"
                                onClick={() => {
                                    askChangeCreditCard();
                                }}
                            >
                                <div className="tw:inline-flex tw:px-6 tw:h-[45px] tw:items-center tw:justify-center tw:gap-3 tw:rounded-[8px] tw:border tw:border-[#D4B055] tw:bg-white tw:px-6 tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:leading-[40px] tw:text-[#D4B055]">
                                    <WarningIcon color="#D4B055" />
                                    <span>Change Credit Card</span>
                                </div>
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => {
                                askCancelBooking();
                            }}
                        >
                            <div className="tw:inline-flex tw:px-6 tw:h-[45px] tw:items-center tw:justify-center tw:gap-3 tw:rounded-[8px] tw:border tw:border-[#EA4335] tw:bg-white tw:px-6 tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:leading-[40px] tw:text-[#EA4335]">
                                <CloseCircleIcon color="#EA4335" />
                                <span>Cancel Booking</span>
                            </div>
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <div className="tw:mb-2 tw:overflow-hidden tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-white">
                        <div className="tw:flex tw:items-center tw:gap-4 tw:p-4">
                            <div className="tw:w-[180px] tw:flex-shrink-0 tw:font-hanken-grotesk tw:text-[15px] tw:font-[700] tw:text-[#4E4E4E]">
                                Payment Status
                            </div>
                            {isChargeFailed ? (
                                <div className="tw:inline-flex tw:min-w-[90px] tw:items-center tw:justify-center tw:rounded-[5px] tw:bg-[#FDECEA] tw:px-4 tw:py-2 tw:font-hanken-grotesk tw:text-[15px] tw:font-[600] tw:text-[#B3261E]">
                                    Charge Failed
                                </div>
                            ) : (
                                <div className="tw:inline-flex tw:min-w-[90px] tw:items-center tw:justify-center tw:rounded-[5px] tw:bg-[#F3F3F3] tw:px-4 tw:py-2 tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:text-[#8D8D8D]">
                                    Pending
                                </div>
                            )}
                        </div>
                    </div>

                    {/* A failed charge has no retry: Flywire took the payment method and declined
                        it, and /charge-token only accepts a booking still pending. Report it
                        rather than offering an action that cannot succeed. */}
                    {isChargeFailed && (
                        <div className="tw:mb-2 tw:overflow-hidden tw:rounded-[5px] tw:border tw:border-[#F3C7C3] tw:bg-[#FDF5F4]">
                            <div className="tw:flex tw:flex-col tw:gap-4 tw:p-4 tw:md:flex-row tw:md:items-start">
                                <div className="tw:w-[180px] tw:flex-shrink-0 tw:font-hanken-grotesk tw:text-[15px] tw:font-[700] tw:text-[#B3261E]">
                                    Charge Failed
                                </div>
                                <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-2 tw:font-hanken-grotesk tw:text-[14px] tw:leading-[20px] tw:text-[#6B2C27]">
                                    <div>
                                        {failedChargeAmount > 0
                                            ? `Flywire declined ${formatJPY(failedChargeAmount)} for this booking.`
                                            : "Flywire declined the payment for this booking."}
                                        {" "}The booking details were saved, but no payment has been collected.
                                    </div>
                                    {failedChargeClientReason && (
                                        <div>
                                            <span className="tw:text-[#8D8D8D]">Reason: </span>
                                            {failedChargeClientReason}
                                        </div>
                                    )}
                                    {failedChargeReason && (
                                        <div className="tw:text-[13px]">
                                            <span className="tw:text-[#8D8D8D]">Flywire detail: </span>
                                            {failedChargeReason}
                                        </div>
                                    )}
                                    <div className="tw:text-[13px] tw:text-[#8D8D8D]">
                                        This booking cannot be charged again from this page. Contact the
                                        customer to arrange payment.
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {!isChargeFailed && (
                        <div className="tw:mb-2 tw:overflow-hidden tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-white">
                            <div className="tw:flex tw:flex-col tw:gap-4 tw:p-4 tw:md:flex-row tw:md:items-start">
                                <div className="tw:w-[180px] tw:flex-shrink-0">
                                    <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[700] tw:text-[#4E4E4E]">Approve</div>
                                    <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:text-[#8D8D8D]">Confirm Booking</div>
                                </div>

                                <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-4 tw:md:flex-row tw:md:items-start tw:md:gap-10">
                                    <div className="tw:flex tw:flex-col tw:items-start tw:gap-2">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                // Bookings tied to a quotation with standalone items can only be
                                                // charged from the quotation detail — open it instead of charging here.
                                                if (booking?.chargeFromQuotation) {
                                                    openQuotationDetail();
                                                    return;
                                                }
                                                confirm(isDeferredPayment
                                                    ? {
                                                        title: "Add this booking to the monthly total?",
                                                        description: `${chargeableAmount.toLocaleString()} JPY is added to the customer's monthly invoice.`,
                                                        confirmLabel: "Add To Monthly Total",
                                                        onConfirm: () => confirmBookingHandler(booking?._id, false),
                                                    }
                                                    : {
                                                        title: "Charge this booking?",
                                                        description: `${chargeableAmount.toLocaleString()} JPY is charged to the card on file.`,
                                                        confirmLabel: "Charge",
                                                        onConfirm: () => chargeTokenHandler(booking?._id, false),
                                                    });
                                            }}
                                        >
                                            {booking?.chargeFromQuotation ? (
                                                <div className="tw:inline-flex tw:h-[40px] tw:min-w-[220px] tw:items-center tw:justify-center tw:gap-2 tw:rounded-[5px] tw:bg-[#FFFFFF] tw:border-1! tw:border-[#D4B055]! tw:px-5 tw:font-hanken-grotesk tw:text-[14px] tw:font-[600] tw:text-[#282828]">
                                                    <span>Charge from the quotation detail</span>
                                                    <LinkIcon size={12} color="#212529" />
                                                </div>
                                            ) : (
                                                <div className={`tw:inline-flex tw:h-[40px] tw:min-w-[220px] tw:items-center tw:justify-center tw:rounded-[5px] tw:px-5 tw:font-hanken-grotesk tw:text-[14px] tw:font-[600] ${getPaymentStatusLabel(effectivePaymentStatus, isDeferredPayment, chargeableAmount)?.className}`}>
                                                    {getPaymentStatusLabel(effectivePaymentStatus, isDeferredPayment, chargeableAmount)?.text}
                                                </div>
                                            )}
                                        </button>

                                        {/* Settle the whole cart in one go. Only shown for a cart holding more
                                            than one booking, where the button above would leave the siblings
                                            unpaid and needing the same action on each of their pages. */}
                                        {isCartSettleable && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    confirm(isDeferredPayment
                                                        ? {
                                                            title: `Add all ${cartBookings.length} bookings in this cart to the monthly total?`,
                                                            description: `${cartChargeableAmount.toLocaleString()} JPY in total. Each booking is filed under its own travel month.`,
                                                            confirmLabel: "Add Whole Cart",
                                                            onConfirm: () => confirmBookingHandler(booking?.cartID, true),
                                                        }
                                                        : {
                                                            title: `Charge all ${cartBookings.length} bookings in this cart together?`,
                                                            description: `${cartChargeableAmount.toLocaleString()} JPY is charged to the card on file as a single payment.`,
                                                            confirmLabel: "Charge Whole Cart",
                                                            onConfirm: () => chargeTokenHandler(booking?.cartID, true),
                                                        });
                                                }}
                                            >
                                                <div className="tw:inline-flex tw:h-[40px] tw:min-w-[220px] tw:items-center tw:justify-center tw:rounded-[5px] tw:border tw:border-[#3CAEA3] tw:bg-white tw:px-5 tw:font-hanken-grotesk tw:text-[14px] tw:font-[600] tw:text-[#3CAEA3]">
                                                    {isDeferredPayment
                                                        ? `¥ Add ${cartChargeableAmount.toLocaleString()} JPY (Whole Cart) To Monthly Total`
                                                        : `¥ Charge ${cartChargeableAmount.toLocaleString()} JPY (Whole Cart)`}
                                                </div>
                                            </button>
                                        )}
                                    </div>

                                    {
                                        !isDeferredPayment ? (
                                            <div className="tw:grid tw:flex-1 tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-3">
                                                <div>
                                                    <div className="tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">Flywire Charge Account</div>
                                                    <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:text-[#282828]">-</div>
                                                </div>
                                                <div>
                                                    <div className="tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">Status</div>
                                                    <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:text-[#282828]">-</div>
                                                </div>
                                                <div>
                                                    <div className="tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">Flywire Token</div>
                                                    <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:text-[#282828]">-</div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="tw:grid tw:flex-1 tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-3">
                                                <div>
                                                    <div className="tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">Booking No. of the Month</div>
                                                    <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:text-[#282828]">-</div>
                                                </div>
                                                <div>
                                                    <div className="tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">Monthly Total Amount</div>
                                                    <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:text-[#282828]">-</div>
                                                </div>
                                            </div>
                                        )
                                    }
                                </div>
                            </div>

                            <div className="tw:h-px tw:w-full tw:bg-[#E8E8E8]" />

                            <div className="tw:flex tw:flex-col tw:gap-4 tw:p-4 tw:md:flex-row">
                                <div className="tw:w-[180px] tw:flex-shrink-0">
                                    <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[700] tw:text-[#4E4E4E]">
                                        <div>Charge an Edited</div>
                                        <div>Booking Cost</div>
                                    </div>
                                    <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:text-[#8D8D8D]">Before the Transfer</div>
                                </div>

                                <div className="tw:flex-1 tw:space-y-3">
                                    <div>
                                        <div className="tw:mb-1 tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">New Amount</div>
                                        <input
                                            type="text"
                                            placeholder="Amount"
                                            className="tw:w-full tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-white tw:px-3 tw:py-2 tw:font-hanken-grotesk tw:!text-[15px] tw:placeholder:!text-[15px] tw:font-[400] tw:text-[#282828] tw:outline-none"
                                            value={newAmount === 0 || newAmount === '0' || newAmount === '' ? '' : Number(newAmount).toLocaleString()}
                                            onChange={(e) => {
                                                const raw = e.target.value.replace(/,/g, '');
                                                if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                                                    setNewAmount(raw);
                                                }
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <div className="tw:mb-1 tw:font-hanken-grotesk tw:text-[13px] tw:font-[400] tw:text-[#8D8D8D]">
                                            Explanation for the Price Adjustment (sent to the client via email)
                                        </div>
                                        <textarea
                                            rows={7}
                                            placeholder="Explanation for the Price Adjustment"
                                            className="tw:!w-full tw:!rounded-[5px] tw:!border tw:!border-[#E8E8E8] tw:!bg-white tw:!px-3 tw:!py-2 tw:!font-hanken-grotesk tw:!text-[15px] tw:!placeholder:text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!outline-none"
                                            value={detailItinerary}
                                            onChange={(e) => setDetailItinerary(e.target.value)}
                                        />
                                    </div>
                                    <div className="tw:font-hanken-grotesk tw:text-[13px] tw:italic tw:font-[400] tw:text-[#8D8D8D]">*Sent to the client via email</div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            confirm(isDeferredPayment
                                                ? {
                                                    title: "Add this booking at the edited cost?",
                                                    description: `${Number(newAmount || 0).toLocaleString()} JPY goes on the monthly invoice instead of the original price, and the customer is emailed the reason.`,
                                                    confirmLabel: "Add Edited Cost",
                                                    onConfirm: () => confirmBookingHandler(booking?._id, false, "EDITED_FEE", newAmount, detailItinerary),
                                                }
                                                : {
                                                    title: "Charge this booking at the edited cost?",
                                                    description: `${Number(newAmount || 0).toLocaleString()} JPY is charged instead of the original price, and the customer is emailed the reason.`,
                                                    confirmLabel: "Charge Edited Cost",
                                                    onConfirm: () => chargeTokenHandler(booking?._id, false, "edited", newAmount, detailItinerary),
                                                });
                                        }}
                                        disabled={(!newAmount || Number(newAmount) === 0) ? true : false}
                                    >
                                        <div className={`
                                            tw:inline-flex
                                            tw:h-[40px]
                                            tw:min-w-[210px]
                                            tw:items-center 
                                            tw:justify-center 
                                            tw:rounded-[5px] 
                                            ${(!newAmount || Number(newAmount) === 0) ? "tw:border tw:border-[#D9D9D9] tw:bg-[#F9F9F9] tw:text-[#B5B5B5]" : "tw:bg-[#3CAEA3] tw:text-[#FFFFFF]"}
                                            tw:px-5 
                                            tw:font-hanken-grotesk 
                                            tw:text-[14px] 
                                            tw:font-[400] 
                                        `}>
                                            {isDeferredPayment ? "Confirm Edited Booking Cost" : "Charge Edited Booking Cost"}
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {
                        false && (
                            <div className="tw:mb-5 tw:overflow-hidden tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-white">
                                <div className="tw:flex tw:flex-col tw:gap-4 tw:p-4 tw:md:flex-row tw:md:items-start">
                                    <div className="tw:w-[180px] tw:flex-shrink-0">
                                        <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[700] tw:text-[#4E4E4E]">Reject</div>
                                        <div className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:text-[#8D8D8D]">No Availability</div>
                                    </div>

                                    <div className="tw:flex-1">
                                        <button type="button">
                                            <div className="tw:inline-flex tw:h-[40px] tw:min-w-[130px] tw:items-center tw:justify-center tw:rounded-[5px] tw:bg-[#E94E3B] tw:px-5 tw:font-hanken-grotesk tw:text-[14px] tw:font-[600] tw:text-white">
                                                No Availability
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )
                    }

                    {hasFailedCharge && (
                        <div className="tw:mt-[52px] tw:gap-2 tw:mb-5 tw:flex tw:justify-end">
                            {!isDeferredPayment && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        askChangeCreditCard();
                                    }}
                                >
                                    <div className="tw:inline-flex tw:px-6 tw:h-[45px] tw:items-center tw:justify-center tw:gap-3 tw:rounded-[8px] tw:border tw:border-[#D4B055] tw:bg-white tw:px-6 tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:leading-[40px] tw:text-[#D4B055]">
                                        <WarningIcon color="#D4B055" />
                                        <span>Change Credit Card</span>
                                    </div>
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    askCancelBooking();
                                }}
                            >
                                <div className="tw:inline-flex tw:px-6 tw:h-[45px] tw:items-center tw:justify-center tw:gap-3 tw:rounded-[8px] tw:border tw:border-[#EA4335] tw:bg-white tw:px-6 tw:font-hanken-grotesk tw:text-[15px] tw:font-[400] tw:leading-[40px] tw:text-[#EA4335]">
                                    <CloseCircleIcon color="#EA4335" />
                                    <span>Cancel Booking</span>
                                </div>
                            </button>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default PaymentDetail;
